import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Calendar, Users, User, LogOut, Menu, X, Loader2, Globe, Bell, CreditCard } from 'lucide-react';
import ConfirmationModal from '../ui/ConfirmationModal';


export default function SidebarNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, psychologist, signOut } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const { userProfile, loading: authLoading } = useAuth();

  const handleLogout = async () => {
    setIsLoggingOut(true);
    const { error } = await signOut();
    
    if (error) {
      alert(`Erro ao sair: ${error?.message}`);
      setIsLoggingOut(false);
    } else {
      navigate('/authentication-hub');
    }
  };

  const handleLogoutClick = () => {
    setIsOpen(false);
    setShowLogoutModal(true);
  };

  const navigationItems = [
    {
      name: 'Agenda',
      path: '/schedule-dashboard',
      icon: Calendar
    },
    {
      name: 'Pacientes',
      path: '/patient-management',
      icon: Users
    },
    {
      name: 'Perfil',
      path: '/profile-management',
      icon: User
    },
    {
      name: 'Notificações',
      path: '/notification-settings-screen',
      icon: Bell
    },
    {
      name: 'Agenda Pública',
      path: '/public-booking-url-management-screen',
      icon: Globe
    },
    {
      name: 'Assinaturas',
      path: '/subscription-management-screen',
      icon: CreditCard
    }
  ];

  return (
    <>
      {/* Mobile Menu Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="lg:hidden fixed top-4 right-4 z-50 p-2 bg-white rounded-lg shadow-lg"
        >
          <Menu className="w-6 h-6 text-gray-600" />
        </button>
      )}
      {/* Overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-[60]"
          onClick={() => setIsOpen(false)}
        />
      )}
      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 right-0 h-[100dvh] max-h-screen w-64 bg-white border-l border-gray-200 z-[70]
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
          lg:left-0 lg:right-auto lg:border-l-0 lg:border-r lg:translate-x-0 lg:h-screen
        `}
      >
        <div className="flex flex-col h-full">
          {/* Header with Profile */}
          <div className="p-4 lg:p-6 border-b border-gray-200 relative flex-shrink-0">
            {/* Close Button - Mobile Only */}
            <button
              onClick={() => setIsOpen(false)}
              className="lg:hidden absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
            
            <div className="flex items-center gap-3">
              {!userProfile?.profile_photo_url ? (
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-semibold text-lg">
                    {psychologist?.full_name?.charAt(0) || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
              ) : (
                <div className="ml-2 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                  <img 
                    src={userProfile?.profile_photo_url} 
                    alt={userProfile?.full_name || 'Foto de perfil'}
                    className="w-full h-full object-cover rounded-full border border-gray-100"
                    onError={() => setProfilePhotoUrl(null)}
                  />                  
                  <span className="text-white font-semibold text-lg">
                    {psychologist?.full_name?.charAt(0) || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {userProfile?.full_name || 'Psicólogo(a)'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.email}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-3 lg:p-4">
            <div className="space-y-1">
              {navigationItems?.map((item) => {
                const Icon = item?.icon;
                const isActive = location?.pathname === item?.path;

                return (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    onClick={() => setIsOpen(false)}
                    className={`
                      flex items-center gap-3 px-4 py-2.5 lg:py-3 rounded-lg transition-colors
                      ${isActive 
                        ? 'bg-indigo-50 text-blue-600' :'text-gray-700 hover:bg-gray-100'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item?.name}</span>
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Logout Button */}
          <div className="p-3 lg:p-4 border-t border-gray-200 flex-shrink-0">
            <button
              onClick={handleLogoutClick}
              disabled={isLoggingOut}
              className="w-full flex items-center justify-center gap-3 px-4 py-2.5 lg:py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoggingOut ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="font-medium">Saindo...</span>
                </>
              ) : (
                <>
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Sair</span>
                </>
              )}
            </button>
          </div>
        </div>
      </aside>

      {/* Logout Confirmation Modal */}
      <ConfirmationModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        title="Sair da conta"
        message="Deseja realmente sair? Você precisará fazer login novamente para acessar o sistema."
        confirmText="Sim, sair"
        cancelText="Retornar"
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
        icon={LogOut}
      />
    </>
  );
}
const SidebarProvider = () => {
  React.useEffect(() => {
    // eslint-disable-next-line no-console
    console.warn('Placeholder: SidebarProvider is not implemented yet.');
  }, []);
  return (
    <div>
      {/* SidebarProvider placeholder */}
    </div>
  );
};

export { SidebarProvider };
function useSidebar(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: useSidebar is not implemented yet.', args);
  return null;
}

export { useSidebar };